import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useProgramVersionDetails } from "@/Common/hooks/Metrics";

// Helpers
import { getFranchiseOEMProgramVersion, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Layouts
import RequiredReferences from "@/Layouts/components/RequiredReferences";

// Custom components
import RequiredMetrics from "@PayPlans/components/RequiredMetrics";

// MUI
import { Grid } from "@mui/material";

const levels = [{ level: "Base" }, { level: "Standard" }, { level: "Potential" }, { level: "Super" }];

const hasAllRequiredMetrics = (checkMetrics = []) => checkMetrics.every((metric) => metric.required);

const updateRequiredTiers = (updatedMetrics = [], tiers = [], metrics = []) => {
	const hasInitializedMetrics = Boolean(updatedMetrics?.length);
	const mandatoryRequired = updatedMetrics.filter((metric) => metric.required).length || 0;

	// Required number limits
	const maxNumberRequired = metrics?.length || 0;
	const minNumberRequired = Math.min(maxNumberRequired, mandatoryRequired) || 0;

	// Remove references from the end
	if (tiers.length > maxNumberRequired) {
		tiers.length = maxNumberRequired || 1;
	}

	return tiers.map((tier) => {
		let numberRequired = tier["numberRequired"] || 1;

		if (minNumberRequired && hasInitializedMetrics) {
			if (minNumberRequired === maxNumberRequired) {
				numberRequired = minNumberRequired;
			} else {
				const leastRequired = minNumberRequired + 1;
				const maxCurrent = Math.min(numberRequired, maxNumberRequired);
				numberRequired = Math.max(maxCurrent, leastRequired);
			}
		} else if (numberRequired > maxNumberRequired) {
			numberRequired = maxNumberRequired;
		}

		return { ...tier, numberRequired };
	});
};

const isAllReferencesValid = (
	references = [],
	definition = { missed: "missed", attained: "attained", numberRequired: "numberRequired" }
) => {
	if (!references?.length) {
		return true;
	}
	const { missed, attained, numberRequired } = definition || {};
	return references.every((reference) => (reference[missed]?.value || reference[attained]?.value) && reference[numberRequired]);
};

const mapModelOEMVersion = (model, args) => {
	const { make } = args ?? {};
	const { oemReference = "", version = "" } = getFranchiseOEMProgramVersion({ ...args, franchise: make });
	return {
		...model,
		oemReference,
		version,
	};
};

export default function PinnacleModel(props) {
	const {
		model = {
			oemReference: "GM",
			version: "",
			metrics: [],
			tiers: [],
		},
		department = "SALES",
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Location
	const location = useSelector((state) => state.location.location);
	const locationOEMs = getSanitatedLocationOEMs(location?.branchesOEMs);

	// Get properly metrics based on program reference
	const { data: programDetails, isLoading } = useProgramVersionDetails({
		department,
		program: "Pinnacle",
		oemReference: model?.oemReference ?? "GM",
		version: model?.version ?? "",
	});
	const { details = undefined } = programDetails ?? {};
	const { metrics = [] } = details ?? {};

	const optionsLevelsDisabled = useMemo(() => {
		if (!metrics?.length) {
			return new Map();
		}
		return metrics.reduce((acc, metric) => {
			if (metric?.levelsDisabled?.length) {
				acc.set(metric.metricName, metric.levelsDisabled);
			}
			return acc;
		}, new Map());
	}, [metrics]);

	// Control to send update actions
	const wasUpdated = useRef(false);
	const isValid = useRef(true);
	const isValidReferences = useRef(true);

	const onModelUpdate = (updatedModel) => {
		const isValidModel = isValid.current && isValidReferences.current && Boolean(updatedModel?.metrics?.length);
		const isAllValid = isValidModel && isAllReferencesValid(updatedModel?.tiers || []);
		onActionValidation?.(isAllValid);
		if (isAllValid && wasUpdated.current) {
			wasUpdated.current = false;
			const { oemReference: programOEMReference, version: programVersion } = programDetails ?? {};
			const updated = mapModelOEMVersion(updatedModel, {
				locationOEMs,
				make: "Cadillac",
				oemReference: programOEMReference,
				version: programVersion,
			});
			onActionUpdate(updated);
		}
	};

	// States
	const [innerModel, setInnerModel] = useCallbackState(null, onModelUpdate);

	const handleChangeTiers = (tiers, action) => {
		const updated = { ...innerModel };
		const updatedTiers = updateRequiredTiers(updated["metrics"], tiers, metrics);
		updated["tiers"] = [...updatedTiers];

		if (action === "adding") {
			setInnerModel(updated);
		} else {
			propagate(updated);
		}
	};

	const handleUpdateMetrics = (updatedMetrics = []) => {
		const updated = { ...innerModel };
		const updatedTiers = updateRequiredTiers(updatedMetrics, updated["tiers"], metrics);

		const allRequiredMetrics = hasAllRequiredMetrics(updatedMetrics);
		if (allRequiredMetrics) {
			updatedMetrics = updatedMetrics.map((metric) => {
				return { ...metric, required: false };
			});
		}

		updated["metrics"] = updatedMetrics;
		updated["tiers"] = updatedTiers;
		propagate(updated);
	};

	const propagate = (updated) => {
		wasUpdated.current = true;
		setInnerModel(updated);
	};

	useEffect(() => {
		setInnerModel(model);
		return () => {
			setInnerModel(null);
			wasUpdated.current = false;
			isValid.current = true;
			isValidReferences.current = true;
		};
	}, [model, setInnerModel]);

	return (
		<Grid container direction="column">
			<Grid item mt={2.5}>
				<RequiredMetrics
					options={metrics}
					totalOptions={metrics?.length}
					metrics={innerModel?.metrics || []}
					metricTitle={"Compliance Metric"}
					useLevels
					complianceLevels={levels}
					optionsLevelsDisabled={optionsLevelsDisabled}
					emptyError={true}
					readOnly={readOnly || isLoading}
					onActionUpdate={(updatedMetrics) => handleUpdateMetrics(updatedMetrics)}
					onActionValidation={(isValidMetrics) => {
						isValid.current = isValidMetrics;
					}}
				/>
			</Grid>

			<RequiredReferences
				metrics={innerModel?.metrics || []}
				tiers={innerModel?.tiers || []}
				labels={{
					required: "Required",
					above: "Attained",
					below: "Missed",
				}}
				loading={false}
				readOnly={readOnly}
				handleChangeTiers={handleChangeTiers}
				onActionValidation={(isReferenceValid) => {
					isValidReferences.current = isReferenceValid;
				}}
			/>
		</Grid>
	);
}
PinnacleModel.propTypes = {
	department: PropTypes.string,
	model: PropTypes.shape({
		oemReference: PropTypes.string,
		version: PropTypes.string,
		metrics: PropTypes.array,
		tiers: PropTypes.array,
	}),
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
